"use strict";

const $ = require('jquery');
const _ = require('lodash');
const Controller = require('../../shared/controller');
const mix = require('./mix');

const hasLoad = _.conforms({load: _.isFunction});
const hasRender = _.conforms({render: _.isFunction});
const hasReload = _.conforms({reload: _.isFunction});
const hasUpdate = _.conforms({update: _.isFunction});

const hasQueryParams = _.conforms({getQueryParams: _.isFunction});
const hasPanelParams = _.conforms({getPanelParams: _.isFunction});
const hasInfoContent = _.conforms({getInfoContent: _.isFunction});
const hasReset = _.conforms({reset: _.isFunction});

class ConfigurePaneComponent {
	constructor(pane, componentID, component, container) {
		this._pane = pane;
		this._id = componentID;
		this._component = component;
		this._container = container;
	}

	get pane() {
		return this._pane;
	}

	get id() {
		return this._id;
	}

	get component() {
		return this._component;
	}

	get options() {
		return this._component.options;
	}

	get container() {
		return this._container;
	}

	set container(c) {
		// TODO: throw here maybe?
		this._container = c;
	}

	render(options, callback) {
		let c = this.component;
		options = Object.assign({}, options, {container: this.container});
		if (hasLoad(c)) {
			return c.load(options, callback);
		} else if (hasRender(c)) {
			return c.render(options, callback);
		}

		// Silently do nothing.
		return callback(null, options);
	}

	update(options, callback) {
		let c = this.component;
		options = Object.assign({}, options);
		if (hasReload(c)) {
			return c.reload(options, callback);
		} else if (hasUpdate(c)) {
			return c.update(options, callback);
		}

		// Silently do nothing.
		return callback(null, options);
	}

	queryParams() {
		let c = this.component;
		if (hasQueryParams(c)) {
			return c.getQueryParams();
		}
		return {};
	}

	paneParams() {
		let c = this.component;
		if (hasPanelParams(c)) {
			return c.getPanelParams();
		}
		return {};
	}

	infoContent() {
		let c = this.component;
		if (hasInfoContent(c)) {
			return c.getInfoContent();
		}
		return '';
	}

	reset() {
		let c = this.component;
		if (hasReset(c)) {
			c.reset();
		}
	}
}

class ConfigurePane extends Controller {
	constructor(options) {
		super(Object.assign({
			paneID: _.uniqueId('cm-configure-pane-'),
			liveUpdates: false
		}, options));
		this._components = new Map();

		// Add components from options
		let components = options.components;
		if (components) {
			for (let componentID of Object.keys(components)) {
				this.addComponent(componentID, components[componentID]);
			}
		}

		_.bindAll(this, 'onRefreshClick', 'onResetClick');
	}

	get components() {
		return this._components;
	}

	* iterComponents() {
		for (let c of this._components.values()) {
			yield c.component;
		}
	}

	addComponent(componentID, component) {
		let c = new ConfigurePaneComponent(this, componentID, component);
		this._components.set(componentID, c);
		return component;
	}

	getComponent(componentID) {
		let c = this._components.get(componentID);
		return c && c.component;
	}

	/**
	 * Find suitable container for component if it's not given,
	 * set it to wrapper and return.
	 *
	 * @param {ConfigurePaneComponent} component
	 * @param {Object} [options]
	 */
	initComponentContainer(component, options) {
		let pane = this.container;
		options = Object.assign({}, component.options, options);
		let container = options.container;
		if (_.isString(container)) {
			// Assuming it's selector, most common case for older panels.
			container = pane.querySelector(container);
		} else if (!container) {
			// Look by componentID, the "proper" way, if template supports it.
			container = pane.querySelector(`[data-component-id="${component.id}"] .cm-component-container`);
		}
		if (!container) {
			throw new ReferenceError(`Container not found: ${component.id}`);
		}
		component.container = container;
		return container;
	}

	/**
	 * Render component, setup listeners.
	 *
	 * @param {ConfigurePaneComponent} component
	 * @param {object} options
	 */
	renderComponent(component, options) {
		options = Object.assign({}, options, {
			params: this.getPaneParams(component),
			container: this.initComponentContainer(component, options)});
		let self = this;
		return component.render(options)
			.then(function () {
				component.component.on('cm:update', function () {
					self.onUpdate(component);
				});
				component.component.on('cm:load', function () {
					self.onLoad(component);
				});
			})
			.catch(function (err) {
				console.error('Unable to render component', component, options);
				console.error(err);
				throw err;
			});
	}

	draw(options) {
		options = super.draw(options);
		let $container = this.$container;
		$container.find('.cm-refresh').click(this.onRefreshClick);
		$container.find('.cm-reset').click(this.onResetClick);
		return options;
	}

	redraw(options, callback) {
		return options;
	}

	render(options) {
		let self = this;
		return super.render(options)
			.then(function (options) {
				let tasks = [];
				for (let c of self.components.values()) {
					tasks.push(self.renderComponent(c, {}));
				}
				return Promise.all(tasks)
					.then(function () {
						return options;
					});
			});
	}

	update(options) {
		let self = this;
		return super.update(options)
			.then(function (options) {
				let params = Object.assign({}, _.get(options, 'params'));
				let tasks = [];
				for (let c of self.components.values()) {
					Object.assign(params, c.paneParams());
					tasks.push(c.update({params: params}));
				}
				return Promise.all(tasks)
					.then(function () {
						return options;
					})
			});
	}

	getQueryParams() {
		let params = {};
		for (let c of this.components.values()) {
			Object.assign(params, c.queryParams());
		}
		return params;
	}

	getPaneParams(component) {
		let params = {};
		for (let c of this.components.values()) {
			if (c !== component) {
				Object.assign(params, c.paneParams());
			}
		}
		return params;
	}

	getInfoContent() {
		let content = {};
		for (let [name, component] of this.components.entries()) {
			content[name] = component.infoContent();
		}
		return content;
	}

	onUpdate(component) {
		console.debug('Component updated', component.queryParams());
		if (this.options.liveUpdates) {
			this.update();
		}
	}

	onLoad(component) {
		this.emit('cm:load');
	}

	onRefreshClick() {
		let self = this;
		this.update()
			.then(
				function () {
					self.emit('cm:update');
				},
				function (err) {
					console.error('Failed to update configure pane');
					console.error(err);
				}
			);
		return false;
	}

	onResetClick() {
		let self = this;
		for (let c of this.components.values()) {
			c.reset();
		}
		this.update({}, function (err) {
			if (!err) {
				self.emit('cm:update');
			}
		});
		return false;
	}
}
exports.ConfigurePane = ConfigurePane;

function CollapsiblePaneMixin(superclass) {
	return class extends superclass {
		constructor(options) {
			super(Object.assign({paneID: _.uniqueId('cm-configure-pane-')}, options));
			_.bindAll(this, 'updateInfo');
		}

		draw(options) {
			let self = this;
			let paneID = this.options.paneID;
			options = super.draw(options);
			this.collapse();
			let $c = this.$container;
			$c.find(`a[href="#${paneID}"]`)
				.click(function () {
					self.toggle();
					return false;
				});
			$c.find('.cm-hide')
				.click(function () {
					self.collapse();
					return false;
				});
			$c.find('.cm-reset')
				.click(function () {
					self.onResetClick();
					return false;
				});
			return options;
		}

		// TODO: this is slow, but it doesn't work otherwise for now.
		get $pane() {
			return this.$container.find(`#${this.options.paneID}`);
		}

		render(options) {
			let self = this;
			return super.render(options)
				.then(function (options) {
					self.updateInfo();
					self.unlock();
					$(self.refreshButton).popover({
						content: 'Click to reload data', placement: 'top',
						trigger: 'manual'
					});
					return options;
				});
		}

		update(options) {
			let self = this;
			this.lock();
			return super.update(options)
				.then(function (options) {
					self.unlock();
					self.updateInfo();
					return options;
				});
		}

		updateInfo() {
			let container = this.container;
			for (let c of this.components.values()) {
				let selector = `.cm-info [data-component-id=${c.id}] .cm-info-content`;
				let componentInfo = container.querySelector(selector);
				if (componentInfo) {
					componentInfo.innerHTML = c.infoContent();
				}
			}
		}

		isShown() {
			return !this.options.collapsed;
		}

		lock() {
			// Disable refresh buttons.
			for (let b of this.container.querySelectorAll('a.cm-refresh')) {
				b.classList.add('disabled');
			}
		}

		unlock() {
			// Enable refresh buttons.
			for (let b of this.container.querySelectorAll('a.cm-refresh')) {
				b.classList.remove('disabled');
			}
		}

		get refreshButton() {
			return this.container.querySelector('.cm-pane a.cm-refresh');
		}

		highlight(component) {
			// highlight component
			// component.container.classList.add('cm-highlight');
			let container = this.container;
			if (component) {
				let c = container.querySelector(`.cm-pane [data-component-id="${component.id}"]`);
				c && c.classList.add('cm-highlight');
			}

			// highlight refresh button
			let b = this.refreshButton;
			if (b) {
				b.classList.add('cm-highlight');
				$(b).popover('show');
			}
		}

		removeHighlight() {
			let container = this.container;
			// Remove highlight from refresh button.
			for (let c of container.querySelectorAll('.cm-pane [data-component-id]')) {
				c.classList.remove('cm-highlight');
			}

			// Remove highlight from components
			let b = this.refreshButton;
			if (b) {
				b.classList.remove('cm-highlight');
				$(b).popover('hide');
			}
		}

		onResetClick() {
			super.onResetClick();
			this.updateInfo();
			return false;
		}

		expand() {
			if (!this.isShown()) {
				this.options.collapsed = false;
				this.$pane.show();
			}
		}

		collapse() {
			this.options.collapsed = true;
			this.$pane.hide();
		}

		toggle() {
			if (this.isShown()) {
				this.collapse();
			} else {
				this.expand();
			}
		}
	}
}

mix.MIXINS.set('COLLAPSIBLE', CollapsiblePaneMixin);
exports.CollapsiblePaneMixin = CollapsiblePaneMixin;
class CollapsiblePane extends CollapsiblePaneMixin(ConfigurePane) {}
exports.CollapsiblePane = CollapsiblePane;
